import AerisWeather from '@aerisweather/javascript-sdk';
import InteractiveMap from '@aerisweather/javascript-sdk/dist/maps/interactive/InteractiveMap';
import { Log } from '../../AuxillaryService/Log';
import { WidgetType } from '../types';
import { Widget } from '../Widget';
import {
    RadarWidgetConfiguration,
    RadarWidgetConfigurationDetails,
    ZodRadarWidgetConfigurationDetails,
} from './types';
import { IMAGE_ASSET_CACHE_IGNORE_CSS_CLASS } from '../../Cache';

export class RadarWidget extends Widget {
    type: WidgetType.RADAR = WidgetType.RADAR;
    declare config: RadarWidgetConfigurationDetails;

    #key = process.env.AERIS_WEATHER_KEY;
    #secret = process.env.AERIS_WEATHER_SECRET;
    /**
     * Aeris InteractiveMap instance
     * @see {@link https://www.aerisweather.com/support/docs/toolkits/aeris-js-sdk/interactive-map/}
     */
    #aerisMap: undefined | InteractiveMap;
    /**
     * Aeris Javascript SDK Instance
     * @see {@link https://www.aerisweather.com/support/docs/toolkits/aeris-js-sdk/}
     */
    #aeris: undefined | AerisWeather;
    /**
     * Interval in seconds for the map to refresh data.
     */
    #refreshMap = 3600;
    /**
     * Element used for the AerisWeather map instance
     */
    #mapElement: undefined | HTMLElement;
    /**
     * Tells whether the aeris map has been initialized on a data refresh
     * @type {boolean}
     */
    #mapInitialized = false;

    /**
     * @param {RadarWidgetConfiguration} widgetConfiguration
     * @param {number} zoneId
     */
    constructor(widgetConfiguration: RadarWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
    }

    /**
     * Checks if object matches ZOD validation and can be parsed.
     * @returns Zod validated and parsed object
     */
    protected validateConfigurationDetails(
        maybeRadarWidgetConfigurationDetails: RadarWidgetConfigurationDetails
    ): RadarWidgetConfigurationDetails {
        return ZodRadarWidgetConfigurationDetails.parse(maybeRadarWidgetConfigurationDetails);
    }

    /**
     * @TODO: If a failure of createMap should throw to the zone or swallow the error.
     * Creates a new dom element that will contain the aeris map. Then it attempts to
     * create the AerisWeather object and await this.createMap().
     */
    async getReadyForDisplay(): Promise<void> {
        this.#mapElement = document.createElement('div');
        this.#mapElement.id = `aeris-map-${this.id}`;
        this.#mapElement.classList.add(IMAGE_ASSET_CACHE_IGNORE_CSS_CLASS);
        this.#mapElement.style.width = `${this.wrapperElement.offsetWidth}px`;
        this.#mapElement.style.height = `${this.wrapperElement.offsetHeight}px`;

        this.wrapperElement.appendChild(this.#mapElement);

        if (!this.#key || !this.#secret) {
            Log.Error(
                `AerisWeather key: AERIS_WEATHER_KEY or secret: AERIS_WEATHER_SECRET are undefined.`,
                null,
                __fileline
            );
        } else {
            this.#aeris = new AerisWeather(this.#key, this.#secret);
            try {
                await this.createMap();
            } catch (err) {
                Log.Error(`AerisWeather map failed to initialize: ${err}`, null, __fileline);
            }
        }
    }

    /**
     * Creates an Aeris Weather Map and registers event listeners on the map
     */
    async createMap(): Promise<void> {
        if (!this.#mapElement) {
            throw new Error('MapElement undefined.');
        }

        if (!this.#aeris) {
            throw new Error('Aeris undefined.');
        }

        const views = await this.#aeris.views();

        if (!views) {
            throw new Error('Unable to initialize aeris views.');
        }

        this.#mapElement.innerHTML = '';

        this.#aerisMap = new views.InteractiveMap('#' + this.#mapElement.id, {
            // TODO: This server parameter seems like it is no longer valid in the external API.
            //   This means we are currently unable to cache shared images between radars.
            server: process.env.DOMAIN_AERIS_WEATHER,
            center: {
                lat: this.config.lat,
                lon: this.config.lng,
            },
            size: {
                width: this.wrapperElement.offsetWidth,
                height: this.wrapperElement.offsetHeight,
            },
            timeline: {
                from: -2 * 3600,
                to: 0,
                duration: 4,
            },
            refresh: this.#refreshMap,
            zoom: this.config.zoom,
            strategy: 'leaflet',
            layers: this.config.layers,
        });

        this.#mapInitialized = false;
        this.#aerisMap?.on('load', this.onAerisMapLoad);
        this.#aerisMap?.on('ready', this.onAerisMapReady);

        const loadPromise = new Promise((resolve) => this.#aerisMap?.once('load', resolve));
        const readyPromise = new Promise((resolve) => this.#aerisMap?.once('ready', resolve));

        await Promise.race([loadPromise, readyPromise]);
    }

    /**
     * @TODO It doesn't seem as if this logic is required any more the
     *   refresh wasn't happening during some testing where it could
     *   re-initialize the map.
     */
    private onAerisMapLoad = async () => {
        if (this.#mapInitialized) {
            //aeris visualizer has bugs after refreshing its data,
            // this is a work around that re initializes the map
            // on the first refresh.
            this.#aerisMap?.timeline.stop();
            this.#aerisMap?.off('load', this.onAerisMapLoad);
            this.#aerisMap?.off('ready', this.onAerisMapReady);
            this.#aerisMap = undefined;

            await this.createMap();
        } else {
            this.#mapInitialized = true;
        }
    };

    private onAerisMapReady = () => {
        this.#aerisMap?.timeline.play();
    };

    /**
     * Stops the aerisMap timeline, turns it off, then removes the reference.
     * Removes the DOM element for the map.
     */
    async stop(): Promise<void> {
        if (this.#aerisMap) {
            this.#aerisMap.timeline.stop();
            this.#aerisMap?.off('load', this.onAerisMapLoad);
            this.#aerisMap?.off('ready', this.onAerisMapReady);
            this.#aerisMap = undefined;
        }

        this.#mapElement?.remove();

        return super.stop();
    }
}
